

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Snackbar,
    Alert,
    Autocomplete,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from 'assets/theme/base/colors';

const NewGuideTripNoteModal = ({ isOpen, onClose, onSave }) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [guides, setGuides] = useState([]);
    const [trips, setTrips] = useState([]);
    const [tripOptions, setTripOptions] = useState([]);
    const [guideAllocations, setGuideAllocations] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        date: new Date().toISOString().split('T')[0],
        guide_id: null,
        guide_allocation_id: null,
        trip_id: null,
        trip_option_id: null,
        did_day_run_according_to_plan: '',
        accidents_injuries: '',
        behaviour_concerns: '',
        weather_issues: '',
        any_other_notes: '',
    });

    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchGuides();
            fetchTrips();
            fetchTripOptions();
            fetchGuideAllocations();
        }
    }, [isOpen]);

    const fetchGuides = async () => {
        try {
            const response = await axiosInstance.get('/accounts/');
            setGuides(response.data.filter(acc => acc.account_type === 'Guide').map(acc => ({ value: acc.id, label: acc.name })));
        } catch (error) {
            console.error('Error fetching guides:', error);
        }
    };

    const fetchTrips = async () => {
        try {
            const response = await axiosInstance.get('/trips/');
            setTrips(response.data.map(trip => ({ value: trip.id, label: trip.name || `Trip ${trip.id}` })));
        } catch (error) {
            console.error('Error fetching trips:', error);
        }
    };

    const fetchTripOptions = async () => {
        try {
            const response = await axiosInstance.get('/trip-options/');
            setTripOptions(response.data.map(opt => ({ value: opt.id, label: opt.name || `Option ${opt.id}` })));
        } catch (error) {
            console.error('Error fetching trip options:', error);
        }
    };

    const fetchGuideAllocations = async () => {
        try {
            const response = await axiosInstance.get('/guide-allocations/');
            setGuideAllocations(response.data.map(alloc => ({ value: alloc.id, label: alloc.name || `Allocation ${alloc.id}` })));
        } catch (error) {
            console.error('Error fetching guide allocations:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.date) errors.date = 'Date is required';
        if (!formData.guide_id) errors.guide_id = 'Guide is required';
        if (!formData.guide_allocation_id) errors.guide_allocation_id = 'Guide Allocation is required';
        if (!formData.trip_id) errors.trip_id = 'Trip is required';
        if (!formData.trip_option_id) errors.trip_option_id = 'Trip Option is required';
        if (!formData.did_day_run_according_to_plan) errors.did_day_run_according_to_plan = 'This field is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                date: new Date().toISOString().split('T')[0],
                guide_id: null,
                guide_allocation_id: null,
                trip_id: null,
                trip_option_id: null,
                did_day_run_according_to_plan: '',
                accidents_injuries: '',
                behaviour_concerns: '',
                weather_issues: '',
                any_other_notes: '',
            });
        } catch (error) {
            console.error('Error saving Guide Trip Note:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Guide Trip Note',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Guide Trip Note</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Date"
                                        name="date"
                                        type="date"
                                        value={formData.date}
                                        onChange={handleInputChange}
                                        error={!!formErrors.date}
                                        helperText={formErrors.date}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={guides}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={guides.find(g => g.value === formData.guide_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, guide_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Guide"
                                                required
                                                error={!!formErrors.guide_id}
                                                helperText={formErrors.guide_id}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={guideAllocations}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={guideAllocations.find(ga => ga.value === formData.guide_allocation_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, guide_allocation_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Guide Allocation"
                                                required
                                                error={!!formErrors.guide_allocation_id}
                                                helperText={formErrors.guide_allocation_id}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={trips}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={trips.find(t => t.value === formData.trip_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, trip_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Trip"
                                                required
                                                error={!!formErrors.trip_id}
                                                helperText={formErrors.trip_id}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={tripOptions}
                                        getOptionLabel={(option) => option?.label || ''}
                                        value={tripOptions.find(to => to.value === formData.trip_option_id) || null}
                                        onChange={(e, newValue) => {
                                            setFormData(prev => ({ ...prev, trip_option_id: newValue?.value || null }));
                                        }}
                                        sx={{ '& .MuiOutlinedInput-root': { height: '35px' } }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Trip Option"
                                                required
                                                error={!!formErrors.trip_option_id}
                                                helperText={formErrors.trip_option_id}
                                                variant="outlined"
                                                margin="normal"
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Did the day run according to plan?"
                                        name="did_day_run_according_to_plan"
                                        value={formData.did_day_run_according_to_plan}
                                        onChange={handleInputChange}
                                        error={!!formErrors.did_day_run_according_to_plan}
                                        helperText={formErrors.did_day_run_according_to_plan}
                                        required
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Accidents/Injuries"
                                        name="accidents_injuries"
                                        value={formData.accidents_injuries}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Behaviour Concerns"
                                        name="behaviour_concerns"
                                        value={formData.behaviour_concerns}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Weather Issues"
                                        name="weather_issues"
                                        value={formData.weather_issues}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Any Other Notes"
                                        name="any_other_notes"
                                        value={formData.any_other_notes}
                                        onChange={handleInputChange}
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                <MDButton onClick={onClose} variant="outlined" color="secondary">
                                    Cancel
                                </MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save'}
                                </MDButton>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Modal>
            <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })}>
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewGuideTripNoteModal;

